import React, { useEffect, useMemo, useState } from "react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Plus, Trash2, Pencil, Star } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import ImageUploader from "../../components/common/ImageUploader";

const LEVEL_LABELS = ["Department", "Category", "Subcategory", "Section"];
const emptyForm = {
  name: "", slug: "", parent_id: null, image_url: "", banner_url: "",
  order: 0, is_featured: false, is_active: true, meta_title: "", meta_description: "",
};

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminCategories() {
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [t, f] = await Promise.all([
        api.get("/categories/tree"),
        api.get("/categories"),
      ]);
      setTree(t.data);
      setFlat(f.data);
      const exp = {};
      const walk = (nodes, d) => nodes.forEach((n) => { if (d <= 1) exp[n.id] = true; walk(n.children || [], d + 1); });
      walk(t.data, 0);
      setExpanded((e) => ({ ...exp, ...e }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = (parent = null) => {
    setEditingId(null);
    setForm({ ...emptyForm, parent_id: parent ? parent.id : null });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "", slug: c.slug || "", parent_id: c.parent_id || null,
      image_url: c.image_url || "", banner_url: c.banner_url || "",
      order: c.order || 0, is_featured: !!c.is_featured, is_active: c.is_active !== false,
      meta_title: c.meta_title || "", meta_description: c.meta_description || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const body = { ...form, slug: form.slug || slugify(form.name), order: Number(form.order) || 0 };
    try {
      if (editingId) await api.put(`/categories/${editingId}`, body);
      else await api.post("/categories", body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const parentOptions = useMemo(() => {
    // exclude self + descendants when editing
    const invalid = new Set(editingId ? [editingId] : []);
    if (editingId) {
      const mark = (id) => {
        flat.filter((x) => x.parent_id === id).forEach((c) => { invalid.add(c.id); mark(c.id); });
      };
      mark(editingId);
    }
    return flat.filter((c) => !invalid.has(c.id) && (c.level ?? 0) < 3);
  }, [flat, editingId]);

  const renderNode = (n, depth = 0) => (
    <div key={n.id} data-testid={`cat-node-${n.slug}`}>
      <div
        className={`flex items-center gap-2 p-3 border-b border-[hsl(var(--kb-border))] ${depth === 0 ? "bg-[hsl(var(--kb-surface-alt))]/40" : "bg-white"}`}
        style={{ paddingLeft: `${16 + depth * 24}px` }}
      >
        {(n.children?.length || 0) > 0 ? (
          <button onClick={() => toggle(n.id)} aria-label="toggle" className="text-[hsl(var(--kb-muted))]">
            {expanded[n.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : <span className="w-[14px]" />}
        {n.image_url && <img src={imgUrl(n.image_url)} alt="" className="w-8 h-8 object-cover rounded-sm" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{n.name}</span>
            {n.is_featured && <Star size={12} className="text-[hsl(var(--kb-primary))] fill-[hsl(var(--kb-primary))]" />}
            <span className="text-[10px] uppercase text-[hsl(var(--kb-muted))]">{LEVEL_LABELS[n.level] || "—"}</span>
            {!n.is_active && <span className="text-[10px] uppercase text-red-600">inactive</span>}
          </div>
          <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">/{(n.path || []).join("/")}</div>
        </div>
        <div className="flex gap-1">
          {(n.level ?? 0) < 3 && (
            <button onClick={() => openNew(n)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`cat-add-child-${n.slug}`}>
              <Plus size={12} />
            </button>
          )}
          <button onClick={() => openEdit(n)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`cat-edit-${n.slug}`}>
            <Pencil size={12} />
          </button>
          <button onClick={() => remove(n)} className="text-red-700 p-1" data-testid={`cat-delete-${n.slug}`} aria-label="delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {expanded[n.id] && (n.children || []).map((c) => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div data-testid="admin-categories-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Taxonomy</div>
          <h1 className="font-display text-4xl">Catalog Tree</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Department → Category → Subcategory → Section</p>
        </div>
        <button className="kb-btn-primary" onClick={() => openNew(null)} data-testid="cat-add-department">
          <Plus size={16} className="inline mr-1" /> Add Department
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))]">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="cat-tree-loading">Loading categories…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="cat-tree-error">{error}</div>
        ) : tree.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No categories yet — add a department to start.</div>
        ) : tree.map((n) => renderNode(n, 0))}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Category"
        subtitle={editingId ? "Edit" : "Create"}
        testid="cat-form-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="cat-save-btn">{editingId ? "Update" : "Create"}</button>
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Name</label>
            <input className="kb-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-input-name" />
          </div>
          <div>
            <label className="overline block mb-1">Slug</label>
            <input className="kb-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} placeholder="auto" data-testid="cat-input-slug" />
          </div>
        </div>
        <div>
          <label className="overline block mb-1">Parent</label>
          <select className="kb-input" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })} data-testid="cat-input-parent">
            <option value="">— None (Department)</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {"— ".repeat(c.level || 0)}{c.name} ({LEVEL_LABELS[c.level] || ""})
              </option>
            ))}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Tile Image (square)</label>
            <ImageUploader
              value={form.image_url ? [form.image_url] : []}
              onChange={(arr) => setForm({ ...form, image_url: arr[0] || "" })}
              max={1}
              aspect="aspect-square"
              label="Upload tile"
              testid="cat-image"
              cropAspect={1}
            />
          </div>
          <div>
            <label className="overline block mb-1">Banner Image (wide)</label>
            <ImageUploader
              value={form.banner_url ? [form.banner_url] : []}
              onChange={(arr) => setForm({ ...form, banner_url: arr[0] || "" })}
              max={1}
              aspect="aspect-[16/6]"
              label="Upload banner"
              testid="cat-banner"
              cropAspect={16 / 6}
            />
          </div>
          <div>
            <label className="overline block mb-1">Order</label>
            <input type="number" className="kb-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </div>
          <div className="flex items-center gap-4 pt-5">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="cat-input-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          </div>
        </div>
        <div className="border-t border-[hsl(var(--kb-border))] pt-4">
          <div className="overline mb-3">SEO Metadata</div>
          <div className="space-y-3">
            <input className="kb-input" placeholder="Meta title" value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} data-testid="cat-input-meta-title" />
            <textarea className="kb-input" rows={3} placeholder="Meta description" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} data-testid="cat-input-meta-desc" />
          </div>
        </div>
      </AdminDialog>
    </div>
  );
}
